import React from "react";
import useAppLanguage from "hooks/useAppLanguage";

function fmt(n, digits = 2) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function valueTone(key, summary, raw) {
  if (key === "level") return levelClass(summary);
  if (key === "pnl" || key === "equity" || key === "free" || key === "withdrawable") {
    const n = Number(raw);
    if (!Number.isFinite(n) || n === 0) return "";
    return n < 0 ? "account-margin-bar__cell--neg" : "account-margin-bar__cell--pos";
  }
  return "";
}

function levelClass(summary) {
  if (!summary || summary.marginLevel == null || summary.usedMargin <= 0) return "";
  const level = Number(summary.marginLevel);
  const call = Number(summary.marginCallPercent ?? 50);
  const liq = Number(summary.liquidationPointPercent ?? 10);
  if (level <= liq) return "account-margin-bar__level--danger";
  if (level <= call) return "account-margin-bar__level--warn";
  return "account-margin-bar__level--ok";
}

/**
 * Margin Module §11.1 — client account bar.
 * @param {"default"|"compact"|"footer"|"history"|"trade"|"mt5Trade"} variant
 * trade = horizontal MT5 strip (chart dock).
 * mt5Trade = vertical MT5 Trade page (Balance / Credit / Equity / Margin / Free / Holding / Level).
 */
export default function AccountMarginBar({
  summary,
  loading = false,
  compact = false,
  variant,
  onRefresh,
  /** Wallet page only — hide on front-page footer / side menu. */
  showWithdrawable = false,
}) {
  const { t } = useAppLanguage();
  const mode = variant || (compact ? "compact" : "default");
  const isHistory = mode === "history";
  const isTrade = mode === "trade";
  const isMt5Trade = mode === "mt5Trade";

  if (!summary && !loading) {
    return (
      <div className={`account-margin-bar account-margin-bar--${mode}`}>
        <div className="account-margin-bar__empty">{t("loading")}</div>
      </div>
    );
  }

  const levelBare =
    summary?.marginLevel == null || summary?.usedMargin <= 0
      ? "—"
      : fmt(summary.marginLevel, 1);
  const level = levelBare === "—" ? "—" : `${levelBare}%`;

  const allCells = [
    { key: "balance", label: t("balance"), value: fmt(summary?.balance), raw: summary?.balance },
    { key: "credit", label: t("credit"), value: fmt(summary?.credit), raw: summary?.credit },
    { key: "equity", label: t("equity"), value: fmt(summary?.equity), raw: summary?.equity },
    { key: "pnl", label: t("pnl"), value: fmt(summary?.pnl), raw: summary?.pnl },
    {
      key: "used",
      label: isMt5Trade ? t("margin") : t("used"),
      value: fmt(summary?.usedMargin),
      raw: summary?.usedMargin,
    },
    {
      key: "free",
      label: isMt5Trade ? t("freeMargin") : t("free"),
      value: fmt(summary?.freeMargin),
      raw: summary?.freeMargin,
    },
    ...(showWithdrawable
      ? [
          {
            key: "withdrawable",
            label: t("withdrawable"),
            value: fmt(summary?.withdrawable),
            raw: summary?.withdrawable,
          },
        ]
      : []),
    { key: "holding", label: t("holding"), value: fmt(summary?.holdingMargin), raw: summary?.holdingMargin },
    {
      key: "level",
      label: isMt5Trade ? t("marginLevel") : t("level"),
      value: isMt5Trade ? levelBare : level,
      raw: summary?.marginLevel,
    },
  ];

  if (summary?.blockedEquity > 0) {
    allCells.push({
      key: "blocked",
      label: "Blocked",
      value: fmt(summary.blockedEquity),
      raw: summary.blockedEquity,
    });
  }

  // History: compact grid. Trade strip / MT5 page: account metrics only.
  const historyKeys = new Set(["balance", "credit", "equity", "pnl", "free", "level"]);
  const tradeKeys = new Set([
    "balance",
    "credit",
    "equity",
    "used",
    "free",
    "holding",
    "level",
    "blocked",
  ]);
  // MT5 Trade page: Balance / Credit / Equity / Margin / Free / Holding / Level
  const mt5TradeKeys = new Set([
    "balance",
    "credit",
    "equity",
    "used",
    "free",
    "holding",
    "level",
  ]);

  const cells = isHistory
    ? allCells.filter((c) => historyKeys.has(c.key))
    : isTrade
      ? allCells.filter((c) => tradeKeys.has(c.key))
      : isMt5Trade
        ? allCells.filter((c) => mt5TradeKeys.has(c.key))
        : allCells;

  const flatTrade = isTrade || isMt5Trade;

  return (
    <div
      className={`account-margin-bar account-margin-bar--${mode}${
        loading ? " account-margin-bar--loading" : ""
      }`}
      role="status"
      aria-live="polite"
    >
      <div className="account-margin-bar__scroll">
        <div
          className={
            isMt5Trade
              ? "account-margin-bar__list"
              : `account-margin-bar__${isHistory ? "grid" : "row"}`
          }
        >
          {cells.map((c) => (
            <div
              key={c.key}
              className={`account-margin-bar__cell account-margin-bar__cell--${c.key} ${
                flatTrade ? "" : valueTone(c.key, summary, c.raw)
              } ${!flatTrade && c.key === "level" ? levelClass(summary) : ""}`}
            >
              <span className="account-margin-bar__label">
                {isTrade ? `${c.label}:` : c.label}
              </span>
              {isMt5Trade && <span className="account-margin-bar__leader" aria-hidden />}
              <span className="account-margin-bar__value">
                {loading && !summary ? "···" : c.value}
              </span>
            </div>
          ))}
        </div>
      </div>
      {onRefresh && !flatTrade && (
        <button
          type="button"
          className="account-margin-bar__refresh"
          disabled={loading}
          onClick={onRefresh}
          title="Refresh margin"
          aria-label="Refresh margin"
        >
          ↻
        </button>
      )}
    </div>
  );
}

/** Primary pill value: Equity (falls back to Free, then Balance). */
export function formatEquityPill(summary) {
  if (!summary) return null;
  const n = summary.equity ?? summary.freeMargin ?? summary.balance;
  if (n == null) return null;
  return fmt(n);
}

export function equityPillColor(summary) {
  if (!summary) return "var(--green)";
  if (Number(summary.equity) < 0 || Number(summary.freeMargin) < 0) return "var(--red)";
  const level = summary.marginLevel;
  if (level != null && summary.usedMargin > 0) {
    if (level <= Number(summary.liquidationPointPercent ?? 10)) return "var(--red)";
    if (level <= Number(summary.marginCallPercent ?? 50)) return "#e6a700";
  }
  return "var(--green)";
}
